/* The Home page: where each report has got to, against where it should be by now.
 * "By now" depends on today's month: a quarterly report should cover the last finished quarter (October -> Q3) and a monthly
 * report the last finished month (October -> September). Works on the report list (no figures needed) and the approval rows. */
import { stageLabel, statusOf } from './approvals.js';
import { MONTH_NAMES, periodOf } from './periods.js';

/** 'monthly' | 'quarterly'. Uses reporting_frequency (or an extra field of that name); otherwise a report that stops at a
 * quarter end is taken as quarterly and any other month end as monthly. */
export function frequencyOf(report) {
  const v = String(report.reporting_frequency || (report.extra || report.extra_fields || {})['Reporting frequency'] || '').trim().toLowerCase();
  if (v.startsWith('month')) return 'monthly';
  if (v.startsWith('quarter')) return 'quarterly';
  return Number(String(report.as_at_date).slice(5, 7)) % 3 === 0 ? 'quarterly' : 'monthly';
}

const monthly = (year, month) => ({ sel: { mode: 'monthly', year, month }, label: `${MONTH_NAMES[month]} ${year}`, index: year * 12 + month });
const quarterly = (year, quarter) => ({ sel: { mode: 'quarterly', year, quarter }, label: `Q${quarter + 1} ${year}`, index: year * 4 + quarter });

/** The last finished period for `frequency` in the month of `today` -> { sel, label, index }. */
export function expectedPeriod(frequency, today = new Date()) {
  const y = today.getFullYear(), m = today.getMonth();
  if (frequency === 'monthly') return m === 0 ? monthly(y - 1, 11) : monthly(y, m - 1);
  const q = Math.floor(m / 3) - 1;
  return q < 0 ? quarterly(y - 1, 3) : quarterly(y, q);
}

/** The last period a report is complete for, from its as-at date (a period counts once the as-at date reaches its last day). */
function reportedPeriod(frequency, asAt) {
  const [y, mo, d] = String(asAt).split('-').map(Number);
  const endsMonth = d >= new Date(y, mo, 0).getDate();
  const month = endsMonth ? mo - 1 : mo - 2;                       // 0-11 of the last complete month (may be -1)
  const year = month < 0 ? y - 1 : y, mi = (month + 12) % 12;
  if (frequency === 'monthly') return monthly(year, mi);
  const q = Math.floor((mi + 1) / 3) - 1;                          // a quarter is complete once its last month is
  return q < 0 ? quarterly(year - 1, 3) : quarterly(year, q);
}

/** One row per report id (its latest as-at), most overdue first.
 * -> { id, partner, manager, frequency, state: 'current'|'behind', behind, reportedTo, expected, stage, approval, finance, account_manager, report }
 * stage/approval are for the period the report has reached; finance / account_manager are the approvals (or null) for the period due now. */
export function buildHome(reports, approvals, today = new Date()) {
  const latest = new Map();
  reports.forEach((r) => {
    const cur = latest.get(r.settlement_report_id);
    if (!cur || r.as_at_date > cur.as_at_date) latest.set(r.settlement_report_id, r);
  });
  const rows = [...latest.values()].map((r) => {
    const frequency = frequencyOf(r);
    const expected = expectedPeriod(frequency, today);
    const reachedAt = reportedPeriod(frequency, r.as_at_date);
    const behind = Math.max(0, expected.index - reachedAt.index);
    const st = statusOf(approvals, r, periodOf(reachedAt.sel));
    const due = reports.filter((x) => x.settlement_report_id === r.settlement_report_id && x.report_year === expected.sel.year)
      .sort((a, b) => b.as_at_date.localeCompare(a.as_at_date))[0];
    const dueStatus = due && !behind ? statusOf(approvals, due, periodOf(expected.sel)) : { finance: null, account_manager: null };
    return { id: r.settlement_report_id, partner: r.client_name || r.company_name || '', manager: r.account_manager || '', frequency,
      state: behind ? 'behind' : 'current', behind, reportedTo: reachedAt, expected, stage: st.stage, approval: stageLabel(st.stage),
      finance: dueStatus.finance, account_manager: dueStatus.account_manager, report: r };
  });
  return rows.sort((a, b) => b.behind - a.behind || a.id.localeCompare(b.id));
}
